<script lang="ts" setup>
import { i18n } from '@/i18n';
import { computed, StyleValue } from 'vue';
import { useForms } from '@/corePagesModule/composables/useForm';
import { FieldItem, ValidateRule } from '@/coreDomainsModule/constant/validateRules';
import { storeToRefs } from 'pinia';
import TextInput from '@/unmanagedBusinessModule/components/TextInput.vue';
import SimpleSelectInput from '@/corePagesModule/components/SelectInput.vue';
import { useUpdateBusinessModalStore } from '../useUpdateBusinessModalStore';
import { commonModalTextMapper } from '../constants';
import { waitLoading } from '@/corePagesModule/components/InfoModal/waitLoading';
import { showInfoModal } from '@/corePagesModule/components/InfoModal/InfoModalManager';
import TextIconButton from '@/corePagesModule/components/TextIconButton.vue';

const store = useUpdateBusinessModalStore();
const { state } = storeToRefs(store);
const { setNewOptionItemType, getAnotherAddingItem, checkDuplicationOfNewOption } = store;

const t = i18n.t;
// eslint-disable-next-line jsdoc/require-jsdoc
const props = withDefaults(defineProps<{ options?: string[]; disabled?: boolean }>(), { options: () => [] });

const emits = defineEmits<{
  (e: 'setOption', type: string, name: string, code?: string): void;
}>();

const VALUE_ONLY_OPTIONS = computed(() => [t('package'), t('manufacturer'), t('endUser')]);

/**
 * 表示する選択肢の数を決定するための変数
 */
const newOptionGridTemplateColumnNumber = computed(() => {
  return state.value.newOption.isAnotherInputShow ? 2 : 1;
});

/**
 * フォーム用の選択肢項目定義
 */
const newOptionFieldItems = computed((): FieldItem[] => {
  const targetArray = ['value', 'option', 'anotherValue', 'anotherOption'];
  const items = targetArray.map((name: string) => {
    return {
      name,
      rules: [ValidateRule.Required],
    };
  });
  return items;
});

/**
 * バリデーションエラー発生時にテキスト/ボタンの位置を調整するためのスタイル
 */
const textStyle = computed(
  (): StyleValue => (Object.values(newOptionFieldState).some((state) => state.isError.value) ? { paddingTop: '12px' } : {})
);
const buttonStyle = computed(
  (): StyleValue => (Object.values(newOptionFieldState).some((state) => state.isError.value) ? { marginTop: '12px' } : {})
);

/**
 * フォーム変数
 */
const {
  fieldState: newOptionFieldState,
  allValidate: newOptionAllValidate,
  resetForm: newOptionResetForm,
} = useForms(newOptionFieldItems.value);

/**
 * 新規選択肢追加処理で、追加ボタンが有効かどうか
 */
const isNewOptionAddButtonActive = computed(() => {
  if (VALUE_ONLY_OPTIONS.value.includes(newOptionFieldState['option'].value.value)) {
    return newOptionFieldState['value'].value.value && newOptionFieldState['option'].value.value;
  }
  return (
    newOptionFieldState['value'].value.value &&
    newOptionFieldState['option'].value.value &&
    newOptionFieldState['anotherValue'].value.value &&
    newOptionFieldState['anotherOption'].value.value
  );
});

/**
 * 新規選択肢追加のinputに入力した際の処理
 * @param e - 変更された値
 * @param kind - どのinputに入力されたか
 */
const updatedNewOptionInput = (e: string, kind: 'value' | 'option' | 'anotherValue') => {
  if (kind === 'option') {
    if (e === newOptionFieldState['option'].value.value) return;
    setNewOptionItemType(e);
    if (!e || VALUE_ONLY_OPTIONS.value.includes(e)) {
      state.value.newOption.isAnotherInputShow = false;
      const value = newOptionFieldState['value'].value.value;
      newOptionResetForm(newOptionFieldItems.value);
      newOptionFieldState['value'].setValue(value);
      newOptionFieldState['option'].setValue(e);
    } else {
      const value = newOptionFieldState['value'].value.value;
      newOptionResetForm(newOptionFieldItems.value);
      newOptionFieldState['value'].setValue(value);
      newOptionFieldState['option'].setValue(e);
      newOptionFieldState['anotherOption'].setValue(getAnotherAddingItem(e) ?? '');
      state.value.newOption.isAnotherInputShow = true;
    }
  } else {
    newOptionFieldState[kind].setValue(e);
  }
};

/**
 * 新規選択肢追加ボタンを押した際の処理
 */
const clickNewOptionAddButton = async () => {
  /**
   * テキストがundefinedだった場合に空文字に変換する関数
   * @param text - テキスト
   */
  const _checkUndefinedText = (text?: string) => {
    return text ? t(text) : '';
  };

  const validateResult = VALUE_ONLY_OPTIONS.value.includes(newOptionFieldState['option'].value.value)
    ? newOptionFieldState['value'].validate(newOptionFieldState['value'].value.value).isValid
    : newOptionAllValidate();
  if (!validateResult) return;

  let value: string, code: string;
  let isAnotherValueCode: boolean;
  if (
    [
      t('warehouseName'),
      t('supplierName'),
      t('businessName'),
      t('package'),
      t('customerName'),
      t('salesDestinationName'),
      t('manufacturer'),
      t('endUser'),
    ].includes(newOptionFieldState['option'].value.value)
  ) {
    isAnotherValueCode = true;
    value = newOptionFieldState['value'].value.value;
    code = newOptionFieldState['anotherValue'].value.value;
  } else {
    isAnotherValueCode = false;
    value = newOptionFieldState['anotherValue'].value.value;
    code = newOptionFieldState['value'].value.value;
  }
  const checkDuplicationResponse = await waitLoading(
    checkDuplicationOfNewOption(value, code),
    _checkUndefinedText(commonModalTextMapper.newOption.loading?.title)
  );
  if (!checkDuplicationResponse) {
    await showInfoModal({
      modalTitle: _checkUndefinedText(commonModalTextMapper.newOption.commonError?.title),
      modalDescription: _checkUndefinedText(commonModalTextMapper.newOption.commonError?.description),
      backButtonLabel: t('確認'),
      icon: 'error',
      colorStyle: 'error',
      enableOutsideClick: false,
      isDisabledNextButton: true,
    });
    return;
  }
  if (checkDuplicationResponse.existsResult) {
    await showInfoModal({
      modalTitle: _checkUndefinedText(commonModalTextMapper.newOption.duplicateError?.title),
      modalDescription: _checkUndefinedText(commonModalTextMapper.newOption.duplicateError?.description),
      backButtonLabel: t('確認'),
      icon: 'error',
      colorStyle: 'error',
      enableOutsideClick: false,
      isDisabledNextButton: true,
    });
    // 重複チェックに引っかかった場合
    if (checkDuplicationResponse.existsValue.toString() === value) {
      newOptionFieldState[isAnotherValueCode ? 'value' : 'anotherValue'].isError.value = true;
      newOptionFieldState[isAnotherValueCode ? 'value' : 'anotherValue'].errorMessage.value = t('重複しています');
    }
    if (checkDuplicationResponse.existsCode === code) {
      newOptionFieldState[isAnotherValueCode ? 'anotherValue' : 'value'].isError.value = true;
      newOptionFieldState[isAnotherValueCode ? 'anotherValue' : 'value'].errorMessage.value = t('重複しています');
    }
  } else {
    const newOptionItemType = state.value.newOption.itemType === t('item') ? 'business' : state.value.newOption.itemType;
    emits('setOption', newOptionItemType, value, code);
    newOptionResetForm(newOptionFieldItems.value);
  }
  return;
};
</script>
<template>
  <div>
    <div class="section__container">
      <p class="section__text">{{ t('項目へ新規選択肢を追加') }}</p>
      <div class="section__input__container">
        <TextInput
          class="section__input"
          :model-value="newOptionFieldState['value'].value.value"
          label=""
          :placeholder="t('新規選択肢')"
          :is-error="newOptionFieldState['value'].isError.value"
          :error-message="newOptionFieldState['value'].errorMessage.value"
          :disabled="disabled"
          @update:model-value="
            (e) => {
              updatedNewOptionInput(e, 'value');
            }
          "
        />
        <span :style="textStyle">{{ t('を') }}</span>
        <SimpleSelectInput
          class="section__input"
          :model-value="newOptionFieldState['option'].value.value"
          :index="99"
          label=""
          :options="options"
          select-box-name="SELECT_COLUMN_FOR_NEW_SELECTION"
          :placeholder="t('必須項目から選択')"
          display-allow
          :disabled="disabled"
          @update:model-value="
            (e) => {
              updatedNewOptionInput(e, 'option');
            }
          "
        />
        <span :style="textStyle"
          >{{ t('として') }}<span v-if="state.newOption.isAnotherInputShow">{{ t('、') }}</span>
        </span>
        <template v-if="state.newOption.isAnotherInputShow">
          <TextInput
            class="section__input"
            :model-value="newOptionFieldState['anotherValue'].value.value"
            label=""
            :placeholder="t('新規選択肢')"
            :is-error="newOptionFieldState['anotherValue'].isError.value"
            :error-message="newOptionFieldState['anotherValue'].errorMessage.value"
            :disabled="disabled"
            @update:model-value="
              (e) => {
                updatedNewOptionInput(e, 'anotherValue');
              }
            "
          />
          <span :style="textStyle">{{ t('を') }}</span>
          <TextInput class="section__input" :model-value="newOptionFieldState['anotherOption'].value.value" label="" disabled />
          <span :style="textStyle">{{ t('として') }}</span>
        </template>
        <TextIconButton
          :style="buttonStyle"
          :text="t('追加')"
          icon="plusIconWhite"
          :prop-disabled="!isNewOptionAddButtonActive"
          @click="clickNewOptionAddButton"
        />
      </div>
      <p class="section__text">{{ t('上記のセクションに新規選択肢を追加できます。') }}</p>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.section {
  &__container {
    display: grid;
    width: 100%;

    padding: 16px;
    border-radius: 8px;
    gap: 8px;

    background-color: $backgroundDefault;
  }
  &__input {
    align-self: end;
    &__container {
      display: grid;
      grid-template-columns: repeat(v-bind(newOptionGridTemplateColumnNumber), 160px max-content 160px max-content) max-content;
      align-items: center;
      gap: 8px;
    }
  }
}
</style>
